import * as React from 'react';
import { useRef, useState, useCallback, useMemo } from 'react';
import {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Quote,
    Code,
    List,
    ListOrdered,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Link2,
    Image,
    Volume2,
    Highlighter,
    RemoveFormatting,
    Loader2,
    Upload,
    Code2,
    FileDown,
    Play,
} from 'lucide-react';
import { useCurrentEditor, useEditorState } from '@tiptap/react';
import { circularDeepEqual } from 'fast-equals';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import { TableGridPicker } from './TableGridPicker';
import { ToolbarButton } from './ToolbarButton';
import { cn } from '../../../lib/utils';
import { kitT, type KitTranslateFn } from '../../../lib/kitTranslate';
import { measureImageWidth, uploadFile } from '../../../lib/tiptap-upload';
import { markdownToTiptap, type TiptapJSON } from '../../../lib/tiptap';
import type { AudioPlayerStyle } from '../../../lib/tiptap-audio';
import { Textarea } from '../../ui/textarea';
import { capLimitBytes, mediaAcceptAttr, useUploadLimits } from '@ai-content-hub/shared-config';

const DEFAULT_COLOR_VALUE = '__default__';

const DEFAULT_HIGHLIGHT_VALUE = '__none__';

type MediaKind = 'image' | 'audio';

const MEDIA_ACCEPT: Record<MediaKind, string> = { image: 'image/*', audio: 'audio/*' };
const MEDIA_MAX_SIZE: Record<MediaKind, number> = {
    image: 10 * 1024 * 1024,
    audio: 50 * 1024 * 1024,
};

function textColorItems(tx: (key: string, fb: string) => string) {
    return [
        { value: DEFAULT_COLOR_VALUE, label: tx('kRttColorDefault', 'Default') },
        { value: 'var(--color-text)', label: tx('kRttColorText', 'Text') },
        { value: 'var(--color-text-muted)', label: tx('kRttColorMuted', 'Muted') },
        { value: 'var(--color-primary)', label: tx('kRttColorPrimary', 'Primary') },
        { value: 'var(--color-cta)', label: tx('kRttColorAccent', 'Accent') },
        { value: 'var(--color-success)', label: tx('kRttColorSuccess', 'Success') },
        { value: 'var(--color-warning)', label: tx('kRttColorWarning', 'Warning') },
        { value: 'var(--color-error)', label: tx('kRttColorError', 'Error') },
        { value: 'var(--color-info)', label: tx('kRttColorInfo', 'Info') },
    ];
}

function highlightColorItems(tx: (key: string, fb: string) => string) {
    return [
        { value: DEFAULT_HIGHLIGHT_VALUE, label: tx('kRttHlNone', 'None') },
        { value: 'var(--color-warning-light)', label: tx('kRttHlYellow', 'Yellow') },
        { value: 'var(--color-success-light)', label: tx('kRttHlGreen', 'Green') },
        { value: 'var(--color-info-light)', label: tx('kRttHlBlue', 'Blue') },
        { value: 'var(--color-error-light)', label: tx('kRttHlPink', 'Pink') },
    ];
}

/** EAA-1736: images carry the measured intrinsic width so they are inserted at
 *  their own size; audio carries the chosen player style instead. */
interface MediaInsertMeta {
    width?: number | null;
    style?: AudioPlayerStyle;
}

interface MediaUploadPopoverProps {
    kind: MediaKind;
    onInsert: (url: string, meta?: MediaInsertMeta) => void;
    trigger: React.ReactNode;
    translate?: KitTranslateFn;
}

const MediaUploadPopover: React.FC<MediaUploadPopoverProps> = ({
    kind,
    onInsert,
    trigger,
    translate,
}) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [urlDraft, setUrlDraft] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    // EAA-1736: audio-only choice — 'full' native controls vs 'minimal' (play
    // button only). Chosen once at insert time; can still be changed later via
    // MediaBubbleMenu or the right-click context menu.
    const [playerStyle, setPlayerStyle] = useState<AudioPlayerStyle>('full');

    const tx = (k: string, fb: string) => kitT(translate, k, fb);
    // EAA-1212: the per-kind cap, lowered to the server's configured media cap when
    // that is smaller; the accept list follows the server's extensions when known.
    const uploadLimits = useUploadLimits();
    const maxSize = capLimitBytes(MEDIA_MAX_SIZE[kind], uploadLimits.mediaMaxSizeMb);
    const accept = mediaAcceptAttr(uploadLimits, kind, MEDIA_ACCEPT[kind]);

    const reset = useCallback(() => {
        setUrlDraft('');
        setUploading(false);
        setError('');
        setPlayerStyle('full');
    }, []);

    const handleOpenChange = useCallback(
        (next: boolean) => {
            setOpen(next);
            if (!next) reset();
        },
        [reset]
    );

    const handleUseUrl = useCallback(async () => {
        const trimmed = urlDraft.trim();
        if (!trimmed) return;
        // EAA-1736: best-effort measure of the intrinsic width so a pasted image
        // URL is sized like an upload instead of stretching to the full width.
        const width = kind === 'image' ? await measureImageWidth(trimmed) : null;
        onInsert(trimmed, { width, style: kind === 'audio' ? playerStyle : undefined });
        handleOpenChange(false);
    }, [urlDraft, kind, playerStyle, onInsert, handleOpenChange]);

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;

            if (file.size > maxSize) {
                const maxMb = String(Math.round(maxSize / 1024 / 1024));
                setError(
                    kitT(translate, 'kRttFileExceedsMax', 'File exceeds {maxMb}MB limit').replace(
                        '{maxMb}',
                        maxMb
                    )
                );
                return;
            }

            setUploading(true);
            setError('');
            // EAA-1736: measure the local file's intrinsic width before upload —
            // the returned URL is a protected storage path that is not loadable
            // without a signed URL, so it cannot be measured afterwards.
            const width = kind === 'image' ? await measureImageWidth(file) : null;
            const result = await uploadFile(file);
            setUploading(false);

            if (result.url) {
                onInsert(result.url, { width, style: kind === 'audio' ? playerStyle : undefined });
                handleOpenChange(false);
            } else {
                setError(result.error || kitT(translate, 'kRttUploadFailed', 'Upload failed'));
            }
        },
        [kind, maxSize, playerStyle, onInsert, handleOpenChange, translate]
    );

    const maxMb = Math.round(maxSize / 1024 / 1024);

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent
                align="start"
                className={cn('rich-text-media-upload-popover w-80 space-y-3')}
            >
                <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    accept={accept}
                    onChange={handleFileChange}
                    disabled={uploading}
                />

                {kind === 'audio' && (
                    <div className="space-y-1.5">
                        <Label className="text-xs font-medium">
                            {tx('kRttAudioPlayerStyle', 'Player style')}
                        </Label>
                        <ToggleGroup
                            type="single"
                            variant="outline"
                            size="sm"
                            value={playerStyle}
                            onValueChange={(value) => {
                                if (value) setPlayerStyle(value as AudioPlayerStyle);
                            }}
                            className="w-full"
                        >
                            <ToggleGroupItem value="full" className="flex-1 gap-1.5 text-xs">
                                <Volume2 className="h-3.5 w-3.5" />
                                {tx('kRttAudioStyleFull', 'Full controls')}
                            </ToggleGroupItem>
                            <ToggleGroupItem value="minimal" className="flex-1 gap-1.5 text-xs">
                                <Play className="h-3.5 w-3.5" />
                                {tx('kRttAudioStyleMinimal', 'Minimal')}
                            </ToggleGroupItem>
                        </ToggleGroup>
                    </div>
                )}

                <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                        {kind === 'image'
                            ? tx('kRttImageUrl', 'Image URL')
                            : tx('kRttAudioUrl', 'Audio URL')}
                    </Label>
                    <div className="flex gap-2">
                        <Input
                            type="text"
                            inputMode="url"
                            autoComplete="off"
                            placeholder={tx('kRttUrlPh', 'https://…')}
                            value={urlDraft}
                            onChange={(e) => setUrlDraft(e.target.value)}
                            disabled={uploading}
                            className="h-8 text-sm"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleUseUrl();
                                }
                            }}
                        />
                        <Button
                            type="button"
                            size="sm"
                            className="h-8 cursor-pointer"
                            disabled={uploading || !urlDraft.trim()}
                            onClick={handleUseUrl}
                        >
                            {tx('kRttInsert', 'Insert')}
                        </Button>
                    </div>
                </div>

                <div className="relative py-0.5 text-center text-xs text-muted-foreground">
                    {tx('kRttOr', 'or')}
                </div>

                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full cursor-pointer gap-2"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                >
                    {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Upload className="h-4 w-4" />
                    )}
                    {uploading
                        ? tx('kRttUploading', 'Uploading…')
                        : tx('kRttChooseFileMax', 'Choose file (max {maxMb}MB)').replace(
                              '{maxMb}',
                              String(maxMb)
                          )}
                </Button>

                {error && <p className="text-xs text-destructive">{error}</p>}
            </PopoverContent>
        </Popover>
    );
};

interface EmbedInsertData {
    url: string;
    width: number | null;
    height: number;
}

interface EmbedUrlPopoverProps {
    onInsert: (data: EmbedInsertData) => void;
    trigger: React.ReactNode;
    translate?: KitTranslateFn;
}

const EmbedUrlPopover: React.FC<EmbedUrlPopoverProps> = ({ onInsert, trigger, translate }) => {
    const [open, setOpen] = useState(false);
    const [urlDraft, setUrlDraft] = useState('');
    const [widthDraft, setWidthDraft] = useState('');
    const [heightDraft, setHeightDraft] = useState('315');

    const tx = (k: string, fb: string) => kitT(translate, k, fb);

    const reset = useCallback(() => {
        setUrlDraft('');
        setWidthDraft('');
        setHeightDraft('315');
    }, []);

    const handleOpenChange = useCallback(
        (next: boolean) => {
            setOpen(next);
            if (!next) reset();
        },
        [reset]
    );

    const handleInsert = useCallback(() => {
        const trimmed = urlDraft.trim();
        if (!trimmed) return;
        const w = widthDraft.trim() ? parseInt(widthDraft, 10) : null;
        const h = heightDraft.trim() ? parseInt(heightDraft, 10) : 315;
        onInsert({
            url: trimmed,
            width: w && !isNaN(w) && w > 0 ? w : null,
            height: !isNaN(h) && h > 0 ? h : 315,
        });
        handleOpenChange(false);
    }, [urlDraft, widthDraft, heightDraft, onInsert, handleOpenChange]);

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent align="start" className="rich-text-embed-popover w-80 space-y-3">
                <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{tx('kRttEmbedUrl', 'Embed URL')}</Label>
                    <p className="text-xs text-muted-foreground">
                        {tx('kRttEmbedHint', 'YouTube, Vimeo, or any embeddable URL')}
                    </p>
                    <Input
                        type="text"
                        inputMode="url"
                        autoComplete="off"
                        placeholder={tx('kRttEmbedPh', 'https://youtube.com/watch?v=…')}
                        value={urlDraft}
                        onChange={(e) => setUrlDraft(e.target.value)}
                        className="h-8 text-sm"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleInsert();
                            }
                        }}
                    />
                </div>

                <div className="flex gap-3">
                    <div className="flex-1 space-y-1">
                        <Label className="text-xs text-muted-foreground">
                            {tx('kRttWidthPx', 'Width (px)')}
                        </Label>
                        <Input
                            type="number"
                            min={200}
                            placeholder={tx('kRttPhAuto', 'auto')}
                            value={widthDraft}
                            onChange={(e) => setWidthDraft(e.target.value)}
                            className="h-8 text-sm"
                        />
                    </div>
                    <div className="flex-1 space-y-1">
                        <Label className="text-xs text-muted-foreground">
                            {tx('kRttHeightPx', 'Height (px)')}
                        </Label>
                        <Input
                            type="number"
                            min={100}
                            placeholder={tx('kRttPh315', '315')}
                            value={heightDraft}
                            onChange={(e) => setHeightDraft(e.target.value)}
                            className="h-8 text-sm"
                        />
                    </div>
                </div>

                <Button
                    type="button"
                    size="sm"
                    className="w-full h-8 cursor-pointer"
                    disabled={!urlDraft.trim()}
                    onClick={handleInsert}
                >
                    {tx('kRttEmbedBtn', 'Embed')}
                </Button>
            </PopoverContent>
        </Popover>
    );
};

interface ImportMarkdownPopoverProps {
    onImport: (content: TiptapJSON) => void;
    trigger: React.ReactNode;
    translate?: KitTranslateFn;
}

const ImportMarkdownPopover: React.FC<ImportMarkdownPopoverProps> = ({
    onImport,
    trigger,
    translate,
}) => {
    const [open, setOpen] = useState(false);
    const [mdDraft, setMdDraft] = useState('');

    const tx = (k: string, fb: string) => kitT(translate, k, fb);

    const reset = useCallback(() => {
        setMdDraft('');
    }, []);

    const handleOpenChange = useCallback(
        (next: boolean) => {
            setOpen(next);
            if (!next) reset();
        },
        [reset]
    );

    const handleImport = useCallback(() => {
        const trimmed = mdDraft.trim();
        if (!trimmed) return;
        // Normalize literal \n / \r\n escape sequences to real newlines
        // (common in JSON-serialized strings or API payloads)
        const normalized = trimmed.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n');

        // Split by paragraph breaks (double-newlines) so each section is
        // converted independently and an empty paragraph node is placed
        // between sections — giving the visible blank line the user expects.
        const sections = normalized.split(/\n{2,}/);
        const allContent: TiptapJSON[] = [];

        for (let i = 0; i < sections.length; i++) {
            const section = sections[i].trim();
            if (!section) continue;
            const sectionDoc = markdownToTiptap(section);
            if (sectionDoc.content) {
                allContent.push(...sectionDoc.content);
            }
            // Insert empty paragraph between sections (not after last)
            if (i < sections.length - 1) {
                allContent.push({ type: 'paragraph' });
            }
        }

        const tiptapJson: TiptapJSON = {
            type: 'doc',
            content: allContent.length > 0 ? allContent : [{ type: 'paragraph' }],
        };

        onImport(tiptapJson);
        handleOpenChange(false);
    }, [mdDraft, onImport, handleOpenChange]);

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
            <PopoverContent align="end" className="rich-text-import-md-popover w-96 space-y-3">
                <div className="space-y-1.5">
                    <Label className="text-xs font-medium">
                        {tx('kRttImportMdLabel', 'Import Markdown')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                        {tx('kRttImportMdHint', 'Paste markdown content to convert to rich text')}
                    </p>
                    <Textarea
                        value={mdDraft}
                        onChange={(e) => setMdDraft(e.target.value)}
                        placeholder={tx('kRttImportMdPh', 'Paste markdown here...')}
                        className="min-h-[120px] text-sm font-mono"
                    />
                </div>

                <Button
                    type="button"
                    size="sm"
                    className="w-full h-8 cursor-pointer"
                    disabled={!mdDraft.trim()}
                    onClick={handleImport}
                >
                    {tx('kRttImportMdBtn', 'Convert & Insert')}
                </Button>
            </PopoverContent>
        </Popover>
    );
};

/** Feature keys that control which toolbar groups are visible. */
export type RichTextToolbarFeature =
    | 'bold' | 'italic' | 'underline' | 'strikethrough'
    | 'blockquote' | 'code' | 'heading'
    | 'bulletList' | 'orderedList' | 'table'
    | 'subscript' | 'superscript'
    | 'align'
    | 'blockStyle'
    | 'color' | 'highlight'
    | 'clearFormatting'
    | 'link' | 'image' | 'audio' | 'embed'
    | 'importMarkdown';

export interface RichTextToolbarProps {
    translate?: KitTranslateFn;
    /** When set, only the listed features show toolbar buttons. Undefined = all. */
    features?: RichTextToolbarFeature[];
}

export const RichTextToolbar: React.FC<RichTextToolbarProps> = ({ translate, features }) => {
    const { editor } = useCurrentEditor();
    // WHY: ProseMirror's Selection holds a circular reference back to
    // Node.type.schema (schema -> node types -> each other -> ...). Without an
    // explicit equalityFn, useEditorState's default plain deepEqual recurses
    // into that cycle and blows the call stack (RangeError: Maximum call
    // stack size exceeded) on every selection change, i.e. every keystroke —
    // this was crashing the essay rich-text editor in the mobile WebView.
    // circularDeepEqual (fast-equals) tracks visited references and is safe.
    useEditorState({
        editor,
        selector: (ctx) => ctx.editor?.state.selection ?? null,
        equalityFn: circularDeepEqual,
    });

    const tx = useCallback((k: string, fb: string) => kitT(translate, k, fb), [translate]);

    const colors = useMemo(() => textColorItems(tx), [tx]);
    const highlightColors = useMemo(() => highlightColorItems(tx), [tx]);

    /** When features is undefined, show all. Otherwise, check the set. */
    const featureSet = useMemo(
        () => (features ? new Set<string>(features) : null),
        [features]
    );
    const has = useCallback(
        (f: RichTextToolbarFeature) => featureSet === null || featureSet.has(f),
        [featureSet]
    );

    if (!editor) return null;

    const handleAddLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt(
            tx('kRttLinkPromptTitle', 'Enter URL'),
            previousUrl || tx('kRttLinkPromptDefault', 'https://')
        );
        if (url) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }
    };

    const handleInsertImage = (url: string, meta?: MediaInsertMeta) => {
        // EAA-1736: insert at the measured intrinsic width (capped) so the image
        // keeps its own size instead of stretching to the full editor column.
        const attrs = meta?.width != null ? { src: url, width: meta.width } : { src: url };
        editor.chain().focus().setImage(attrs).run();
    };

    const handleInsertAudio = (url: string, meta?: MediaInsertMeta) => {
        (editor.chain().focus() as ReturnType<typeof editor.chain>)
            .setAudio({ src: url, style: meta?.style })
            .run();
    };

    const handleInsertEmbed = (data: EmbedInsertData) => {
        (editor.chain().focus() as ReturnType<typeof editor.chain>)
            .setEmbed({ src: data.url, width: data.width, height: data.height })
            .run();
    };

    const handleImportMarkdown = (content: TiptapJSON) => {
        editor.commands.setContent(content);
    };

    const setBlockStyle = (value: string) => {
        if (value === 'paragraph') {
            editor.chain().focus().setParagraph().run();
        } else if (value.startsWith('heading')) {
            const level = parseInt(value.replace('heading', ''), 10) as 1 | 2 | 3 | 4;
            editor.chain().focus().setHeading({ level }).run();
        }
    };

    const getBlockStyle = () => {
        if (editor.isActive('heading', { level: 1 })) return 'heading1';
        if (editor.isActive('heading', { level: 2 })) return 'heading2';
        if (editor.isActive('heading', { level: 3 })) return 'heading3';
        if (editor.isActive('heading', { level: 4 })) return 'heading4';
        return 'paragraph';
    };

    const showFormatGroup = has('bold') || has('italic') || has('underline') || has('strikethrough');
    const showBlockGroup = has('blockquote') || has('code') || has('heading');
    const showListGroup = has('bulletList') || has('orderedList') || has('table');
    const showScriptGroup = has('subscript') || has('superscript');
    const showAlignGroup = has('align');
    const showStyleGroup = has('blockStyle');
    const showColorGroup = has('color') || has('highlight');
    const showMediaGroup = has('link') || has('image') || has('audio') || has('embed') || has('importMarkdown');

    return (
        <div className="rich-text-toolbar mb-2 flex flex-wrap items-center gap-1 border-b border-border-surface/40 py-1">
            {showFormatGroup && (
            <div className="flex items-center gap-0.5">
                {has('bold') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive('bold')}
                    aria-label={tx('kRttAriaBold', 'Bold')}
                >
                    <Bold className="h-4 w-4" />
                </ToolbarButton>
                )}
                {has('italic') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive('italic')}
                    aria-label={tx('kRttAriaItalic', 'Italic')}
                >
                    <Italic className="h-4 w-4" />
                </ToolbarButton>
                )}
                {has('underline') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    isActive={editor.isActive('underline')}
                    aria-label={tx('kRttAriaUnderline', 'Underline')}
                >
                    <Underline className="h-4 w-4" />
                </ToolbarButton>
                )}
                {has('strikethrough') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    isActive={editor.isActive('strike')}
                    aria-label={tx('kRttAriaStrike', 'Strikethrough')}
                >
                    <Strikethrough className="h-4 w-4" />
                </ToolbarButton>
                )}
            </div>
            )}

            {(showFormatGroup && (showBlockGroup || showListGroup)) && (
            <div className="w-px h-6 bg-border mx-1" />
            )}

            {showBlockGroup && (
            <div className="flex items-center gap-0.5">
                {has('blockquote') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    isActive={editor.isActive('blockquote')}
                    aria-label={tx('kRttAriaQuote', 'Blockquote')}
                >
                    <Quote className="h-4 w-4" />
                </ToolbarButton>
                )}
                {has('code') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    isActive={editor.isActive('code')}
                    aria-label={tx('kRttAriaCode', 'Inline code')}
                >
                    <Code className="h-4 w-4" />
                </ToolbarButton>
                )}
                {has('heading') && (<>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    isActive={editor.isActive('heading', { level: 1 })}
                    aria-label={tx('kRttAriaH1', 'Heading 1')}
                >
                    <span className="text-xs font-bold">H1</span>
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    isActive={editor.isActive('heading', { level: 2 })}
                    aria-label={tx('kRttAriaH2', 'Heading 2')}
                >
                    <span className="text-xs font-bold">H2</span>
                </ToolbarButton>
                </>)}
            </div>
            )}

            {showListGroup && (
            <div className="flex items-center gap-0.5">
                {has('bulletList') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive('bulletList')}
                    aria-label={tx('kRttAriaBullet', 'Bullet list')}
                >
                    <List className="h-4 w-4" />
                </ToolbarButton>
                )}
                {has('orderedList') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive('orderedList')}
                    aria-label={tx('kRttAriaOrdered', 'Numbered list')}
                >
                    <ListOrdered className="h-4 w-4" />
                </ToolbarButton>
                )}
                {has('table') && (
                <TableGridPicker translate={translate} />
                )}
            </div>
            )}

            {showScriptGroup && (
            <div className="flex items-center gap-0.5">
                {has('subscript') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleSubscript().run()}
                    isActive={editor.isActive('subscript')}
                    aria-label={tx('kRttAriaSub', 'Subscript')}
                >
                    <span className="text-xs">x₂</span>
                </ToolbarButton>
                )}
                {has('superscript') && (
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleSuperscript().run()}
                    isActive={editor.isActive('superscript')}
                    aria-label={tx('kRttAriaSuper', 'Superscript')}
                >
                    <span className="text-xs">x²</span>
                </ToolbarButton>
                )}
            </div>
            )}

            {showAlignGroup && (<>
            <div className="w-px h-6 bg-border mx-1" />
            <div className="flex items-center gap-0.5">
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    isActive={editor.isActive({ textAlign: 'left' })}
                    aria-label={tx('kRttAriaAlignL', 'Align left')}
                >
                    <AlignLeft className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    isActive={editor.isActive({ textAlign: 'center' })}
                    aria-label={tx('kRttAriaAlignC', 'Align center')}
                >
                    <AlignCenter className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    isActive={editor.isActive({ textAlign: 'right' })}
                    aria-label={tx('kRttAriaAlignR', 'Align right')}
                >
                    <AlignRight className="h-4 w-4" />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                    isActive={editor.isActive({ textAlign: 'justify' })}
                    aria-label={tx('kRttAriaAlignJ', 'Justify')}
                >
                    <AlignJustify className="h-4 w-4" />
                </ToolbarButton>
            </div>
            </>)}

            {showStyleGroup && (
            <div className="flex items-center gap-0.5">
                <Select value={getBlockStyle()} onValueChange={setBlockStyle}>
                    <SelectTrigger className="w-[100px] h-8 border-0 bg-transparent shadow-none">
                        <SelectValue placeholder={tx('kRttPhStyle', 'Style')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="paragraph">
                            {tx('kRttStyleNormal', 'Normal')}
                        </SelectItem>
                        <SelectItem value="heading1">
                            {tx('kRttStyleH1', 'Heading 1')}
                        </SelectItem>
                        <SelectItem value="heading2">
                            {tx('kRttStyleH2', 'Heading 2')}
                        </SelectItem>
                        <SelectItem value="heading3">
                            {tx('kRttStyleH3', 'Heading 3')}
                        </SelectItem>
                        <SelectItem value="heading4">
                            {tx('kRttStyleH4', 'Heading 4')}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            )}

            {showColorGroup && (
            <div className="flex items-center gap-0.5">
                {has('color') && (
                <Select
                    value={editor.getAttributes('textStyle').color || DEFAULT_COLOR_VALUE}
                    onValueChange={(value) =>
                        editor
                            .chain()
                            .focus()
                            .setColor(value === DEFAULT_COLOR_VALUE ? '' : value)
                            .run()
                    }
                >
                    <SelectTrigger className="w-[90px] h-8 border-0 bg-transparent shadow-none">
                        <SelectValue placeholder={tx('kRttPhColor', 'Color')} />
                    </SelectTrigger>
                    <SelectContent>
                        {colors.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                                {c.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                )}
                {has('highlight') && (
                <Select
                    value={editor.getAttributes('highlight').color || DEFAULT_HIGHLIGHT_VALUE}
                    onValueChange={(value) =>
                        editor
                            .chain()
                            .focus()
                            .toggleHighlight({
                                color: value === DEFAULT_HIGHLIGHT_VALUE ? undefined : value,
                            })
                            .run()
                    }
                >
                    <SelectTrigger className="w-[90px] h-8 border-0 bg-transparent shadow-none">
                        <Highlighter className="h-4 w-4" />
                    </SelectTrigger>
                    <SelectContent>
                        {highlightColors.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                                {c.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                )}
            </div>
            )}

            {has('clearFormatting') && (
            <ToolbarButton
                onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                aria-label={tx('kRttAriaClearFmt', 'Clear formatting')}
            >
                <RemoveFormatting className="h-4 w-4" />
            </ToolbarButton>
            )}

            {showMediaGroup && (<>
            <div className="w-px h-6 bg-border mx-1" />
            <div className="flex items-center gap-0.5">
                {has('link') && (
                <ToolbarButton
                    onClick={handleAddLink}
                    isActive={editor.isActive('link')}
                    aria-label={tx('kRttAriaLink', 'Link')}
                >
                    <Link2 className="h-4 w-4" />
                </ToolbarButton>
                )}
                {has('image') && (
                <MediaUploadPopover
                    kind="image"
                    onInsert={handleInsertImage}
                    translate={translate}
                    trigger={
                        <ToolbarButton aria-label={tx('kRttAriaImage', 'Image')}>
                            <Image className="h-4 w-4" />
                        </ToolbarButton>
                    }
                />
                )}
                {has('audio') && (
                <MediaUploadPopover
                    kind="audio"
                    onInsert={handleInsertAudio}
                    translate={translate}
                    trigger={
                        <ToolbarButton aria-label={tx('kRttAriaAudio', 'Audio')}>
                            <Volume2 className="h-4 w-4" />
                        </ToolbarButton>
                    }
                />
                )}
                {has('embed') && (
                <EmbedUrlPopover
                    onInsert={handleInsertEmbed}
                    translate={translate}
                    trigger={
                        <ToolbarButton aria-label={tx('kRttAriaEmbed', 'Embed')}>
                            <Code2 className="h-4 w-4" />
                        </ToolbarButton>
                    }
                />
                )}
                {has('importMarkdown') && (
                <ImportMarkdownPopover
                    onImport={handleImportMarkdown}
                    translate={translate}
                    trigger={
                        <ToolbarButton aria-label={tx('kRttAriaImportMd', 'Import Markdown')}>
                            <FileDown className="h-4 w-4" />
                        </ToolbarButton>
                    }
                />
                )}
            </div>
            </>)}
        </div>
    );
};

RichTextToolbar.displayName = 'RichTextToolbar';
